/**
 * AiSettingsPage — PMA-AI-008 (settings) + PMA-AI-009 (playground tester).
 *
 * Route: /admin/ai-settings (admin-only via ProtectedRoute in App.tsx)
 *
 * Security invariant (DELTA-21): API keys are NEVER displayed in full after
 * save. The masked form (e.g. "****abcd") is the only value ever shown.
 * New keys are tracked in separate local state and cleared after a save
 * response; the masked form from the server response is then displayed.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AiModelInfo,
  AiSettings,
  AiSettingsPutBody,
  AiStatus,
  DEFAULT_SETTINGS,
  DEFAULT_TOOL_ALLOWLIST,
  ModelsResponse,
  PlaygroundResponse,
  getAiModels,
  getAiSettings,
  getAiStatus,
  getModelNotToolCapableDetail,
  postPlayground,
  putAiSettings,
  validateAiKey,
  verifyModel,
} from "../lib/aiAdminApi";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

// ── Constants ─────────────────────────────────────────────────────────────────

const PROVIDERS = ["gemini", "openrouter", "anthropic"] as const;
type Provider = (typeof PROVIDERS)[number];

const PROVIDER_LABELS: Record<Provider, string> = {
  gemini: "Gemini",
  openrouter: "OpenRouter",
  anthropic: "Anthropic",
};

/** Record with one entry per provider, derived from PROVIDERS. */
const perProvider = <T,>(make: () => T): Record<string, T> =>
  Object.fromEntries(PROVIDERS.map((p) => [p, make()]));

const FEATURE_KEYS = [
  "narrative",
  "anomaly_sweep",
  "nl_query",
  "match_sheet_import",
] as const;
type FeatureKey = (typeof FEATURE_KEYS)[number];

const TOOL_ALLOWLIST_OPTIONS = DEFAULT_TOOL_ALLOWLIST;

/** Radix `Select.Item` forbids an empty-string value (reserved for
 *  "nothing selected") — same sentinel pattern as `ReferenceValueSelect`. */
const UNSET = "__unset__";

// ── Helpers ───────────────────────────────────────────────────────────────────

/** True when the value looks like a masked key (starts with ****). */
const isMasked = (v: string) => v.startsWith("****");

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  const data = (err as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail;
  if (typeof data === "string") return data;
  return "An unexpected error occurred";
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ToggleProps {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}

function Toggle({ id, checked, onChange, label, disabled }: ToggleProps) {
  return (
    <label
      htmlFor={id}
      className="flex items-center gap-3 cursor-pointer select-none"
    >
      <button
        id={id}
        data-testid={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? "bg-primary-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform duration-200 ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );
}

// ── Playground section ────────────────────────────────────────────────────────

interface PlaygroundProps {
  activeProvider: string;
  availableModels: Record<string, AiModelInfo[]>;
}

/** Small "this model accepts image input" badge, shared by the settings
 *  model selectors and the playground model select (EPIC-95 amendment). */
function ImageCapableBadge({
  provider,
  modelId,
  label,
}: {
  provider: string;
  modelId: string;
  label: string;
}) {
  return (
    <span
      className="rounded bg-primary-50 px-1 py-0.5 text-[10px] font-medium text-primary-700"
      data-testid={`model-image-badge-${provider}-${modelId}`}
    >
      {label}
    </span>
  );
}

function PlaygroundSection({
  activeProvider,
  availableModels,
}: PlaygroundProps) {
  const { t } = useTranslation("admin");

  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState<Provider>(
    (activeProvider as Provider) ?? "gemini",
  );
  const [model, setModel] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<PlaygroundResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  const modelsForProvider = availableModels[provider] ?? [];

  const handleRun = async () => {
    if (!prompt.trim() || isLoading) return;
    setIsLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await postPlayground({
        prompt: prompt.trim(),
        provider,
        model: model || undefined,
      });
      setResult(res);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-6"
      data-testid="playground-section"
    >
      <h2 className="text-lg font-semibold text-gray-900 mb-1">
        {t("aiSettings.playgroundTitle")}
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        {t("aiSettings.playgroundSubtitle")}
      </p>

      {/* Provider + model row */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div>
          <label
            htmlFor="pg-provider"
            className="block text-xs font-medium text-gray-600 mb-1"
          >
            {t("aiSettings.provider")}
          </label>
          <Select
            value={provider}
            onValueChange={(next) => {
              setProvider(next as Provider);
              setModel("");
            }}
            disabled={isLoading}
          >
            <SelectTrigger
              id="pg-provider"
              className="px-3 py-1.5 text-sm"
              data-testid="pg-provider-select"
            >
              <SelectValue>{PROVIDER_LABELS[provider] ?? provider}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PROVIDERS.map((p) => (
                <SelectItem key={p} value={p}>
                  {PROVIDER_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {modelsForProvider.length > 0 && (
          <div>
            <label
              htmlFor="pg-model"
              className="block text-xs font-medium text-gray-600 mb-1"
            >
              {t("aiSettings.model")}
            </label>
            <Select
              value={model === "" ? UNSET : model}
              onValueChange={(next) => setModel(next === UNSET ? "" : next)}
              disabled={isLoading}
            >
              <SelectTrigger
                id="pg-model"
                className="px-3 py-1.5 text-sm"
                data-testid="pg-model-select"
              >
                <SelectValue>
                  {model === "" ? t("aiSettings.defaultModel") : model}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET}>
                  {t("aiSettings.defaultModel")}
                </SelectItem>
                {modelsForProvider.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    <span className="flex items-center gap-1.5">
                      <span>{m.id}</span>
                      {m.supports_images && (
                        <ImageCapableBadge
                          provider={`pg-${provider}`}
                          modelId={m.id}
                          label={t("aiSettings.supportsImages")}
                        />
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Prompt */}
      <div className="mb-3">
        <label
          htmlFor="pg-prompt"
          className="block text-xs font-medium text-gray-600 mb-1"
        >
          {t("aiSettings.prompt")}
        </label>
        <textarea
          id="pg-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={isLoading}
          rows={4}
          placeholder={t("aiSettings.promptPlaceholder")}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:bg-gray-50 resize-y"
          data-testid="pg-prompt-input"
        />
      </div>

      {/* Run button */}
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={handleRun}
          disabled={isLoading || !prompt.trim()}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="pg-run-btn"
        >
          {isLoading ? t("aiSettings.running") : t("aiSettings.run")}
        </button>

        {result && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showRaw}
              onChange={(e) => setShowRaw(e.target.checked)}
              className="form-checkbox h-4 w-4 text-primary-600 rounded"
              data-testid="pg-raw-toggle"
            />
            <span className="text-sm text-gray-600">
              {t("aiSettings.rawJson")}
            </span>
          </label>
        )}
      </div>

      {/* Error box — 502 / 429 / 503 */}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 mb-4"
          data-testid="pg-error-box"
        >
          <p className="font-medium">{t("aiSettings.playgroundError")}</p>
          <p className="mt-1">{error}</p>
        </div>
      )}

      {/* Response viewer */}
      {result && (
        <div
          className="rounded-lg border border-gray-200 bg-gray-50 p-4"
          data-testid="pg-response"
        >
          {showRaw ? (
            <pre
              className="text-xs text-gray-800 overflow-x-auto whitespace-pre-wrap"
              data-testid="pg-raw-json"
            >
              <code>{JSON.stringify(result, null, 2)}</code>
            </pre>
          ) : (
            <>
              <p
                className="text-sm text-gray-900 whitespace-pre-wrap mb-3"
                data-testid="pg-result-text"
              >
                {result.text}
              </p>
              <div
                className="flex flex-wrap gap-4 text-xs text-gray-500"
                data-testid="pg-token-usage"
              >
                <span>
                  {t("aiSettings.totalTokens")}:{" "}
                  <strong className="text-gray-700">
                    {result.token_usage.total_tokens}
                  </strong>
                </span>
                {result.token_usage.prompt_tokens !== undefined && (
                  <span>
                    {t("aiSettings.promptTokens")}:{" "}
                    <strong className="text-gray-700">
                      {result.token_usage.prompt_tokens}
                    </strong>
                  </span>
                )}
                {result.token_usage.completion_tokens !== undefined && (
                  <span>
                    {t("aiSettings.completionTokens")}:{" "}
                    <strong className="text-gray-700">
                      {result.token_usage.completion_tokens}
                    </strong>
                  </span>
                )}
                <span>
                  {t("aiSettings.latencyMs")}:{" "}
                  <strong className="text-gray-700">{result.latency_ms}</strong>
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

/**
 * Per-feature rows shared by the off banner (differing reasons) and the
 * compact list shown while some features are on.
 */
function AiFeatureRows({ status, t }: { status: AiStatus; t: StatusT }) {
  return (
    <ul className="mt-1 space-y-0.5" data-testid="ai-status-features">
      {status.features.map((f) => (
        <li key={f.feature} data-testid={`ai-status-feature-${f.feature}`}>
          <span className="font-medium">
            {t(`aiSettings.feature_${f.feature}`)}
          </span>
          {": "}
          {f.active
            ? t("aiSettings.statusFeatureOn")
            : t("aiSettings.statusFeatureOff")}
          {!f.active && f.reason && <> ({localizeStatusReason(f.reason, t)})</>}
        </li>
      ))}
    </ul>
  );
}

/**
 * Maps the gate's fixed English reasons (ai_settings_service.is_feature_blocked)
 * to i18n keys. Anything unrecognised is shown as the server sent it.
 */
type StatusT = (key: string, options?: Record<string, unknown>) => string;

/** Reasons a saved provider key (or toggle) can fix; the rest need an operator. */
const MISSING_KEY_RE = /^No API key stored for provider '(.+)'$/;
const FEATURE_OFF_RE = /^Feature '(.+)' is disabled$/;
const NOT_CONFIGURED_REASON =
  "AI features not configured (no ai_settings document)";

function isAdminFixable(reason: string): boolean {
  return (
    reason === NOT_CONFIGURED_REASON ||
    FEATURE_OFF_RE.test(reason) ||
    MISSING_KEY_RE.test(reason)
  );
}

function localizeStatusReason(reason: string, t: StatusT): string {
  const missingKey = MISSING_KEY_RE.exec(reason);
  if (missingKey) {
    return t("aiSettings.statusReasonMissingKey", { provider: missingKey[1] });
  }
  if (reason === "AI features disabled: missing AI_SETTINGS_ENCRYPTION_KEY") {
    return t("aiSettings.statusReasonNoEncryptionKey");
  }
  if (reason === NOT_CONFIGURED_REASON) {
    return t("aiSettings.statusReasonNotConfigured");
  }
  const env = /^AI features disabled in '(.+)' environment$/.exec(reason);
  if (env) return t("aiSettings.statusReasonEnvironment", { env: env[1] });
  const feature = FEATURE_OFF_RE.exec(reason);
  if (feature) {
    return t("aiSettings.statusReasonFeatureDisabled", {
      feature: feature[1],
    });
  }
  return reason;
}

export default function AiSettingsPage() {
  const { t } = useTranslation("admin");

  // ── Load state ───────────────────────────────────────────────────────────
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [encryptionWarning, setEncryptionWarning] = useState<string | null>(
    null,
  );

  // ── Form state ───────────────────────────────────────────────────────────
  const [settings, setSettings] = useState<AiSettings>(DEFAULT_SETTINGS);

  /**
   * Raw key input values per provider.  Initialized from api_key_masked once
   * settings load, and reset to the new masked value after each save.
   *
   * Security rule: if the current value passes isMasked() (starts with "****")
   * it is NEVER forwarded to the PUT body — only a freshly-typed plaintext key
   * (non-masked, non-empty) is sent.
   */
  const [keyInputValues, setKeyInputValues] = useState<Record<string, string>>(
    perProvider(() => ""),
  );

  // ── Save state ───────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ── Validate-key state (loading / ok / error feedback only) ──────────────
  // Model lists live in `preloadedModels` below so mount-fetches and explicit
  // validations cannot race-overwrite each other through this shared object.
  const [validateState, setValidateState] = useState<
    Record<
      string,
      {
        loading: boolean;
        ok: boolean | null;
        error: string | null;
      }
    >
  >(perProvider(() => ({ loading: false, ok: null, error: null })));

  // ── Remove-key state (EPIC-115): inline confirmation, no window.confirm ───
  const [confirmingRemove, setConfirmingRemove] = useState<string | null>(null);
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeSuccess, setRemoveSuccess] = useState<string | null>(null);

  // ── Model lists per provider (isolated from validateState) ────────────────
  // Written by the quiet mount-fetch AND by explicit "Validar Clave" clicks.
  // Kept separate so neither path can overwrite the other's per-provider key.
  const [preloadedModels, setPreloadedModels] = useState<
    Record<string, AiModelInfo[]>
  >(perProvider<AiModelInfo[]>(() => []));

  // ── Accumulated models for the Playground section ─────────────────────────
  const availableModels = Object.fromEntries(
    PROVIDERS.map((p) => [p, preloadedModels[p]]),
  );

  // ── Stored-model validity (EPIC-95 amendment, judge round 4) ──────────────
  // Every provider's STORED default_model is verified exactly once, on load,
  // via the same live forced-tool-call probe `verifyModel` runs on save —
  // whether or not that model still appears in the freshly-fetched
  // tool-capable list. Being listed does NOT prove a model still passes the
  // probe: the Gemini native list carries no "still callable" signal (a
  // retired id can stay listed), and a stored id can be missing from the
  // list for reasons unrelated to retirement (a stale `models/`-prefixed id,
  // a listing fetch that failed). One verify call per provider that has a
  // stored model — never a probe of the other list entries — fired from the
  // settings-load effect itself (using the just-fetched response, not the
  // live `settings` state the model-select dropdown edits) so picking a
  // different model before Save can never re-trigger it.
  const [storedModelWarnings, setStoredModelWarnings] = useState<
    Record<string, { model: string; message: string } | null>
  >(perProvider(() => null));

  // ── AI on/off status (EPIC-91) ────────────────────────────────────────────
  // Purely informational: a failed call leaves `aiStatus` null and the page
  // renders exactly as before.
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
  const statusAliveRef = useRef(true);
  const refreshAiStatus = async () => {
    try {
      const next = await getAiStatus();
      if (statusAliveRef.current) setAiStatus(next);
    } catch {
      /* status is advisory; keep whatever is shown */
    }
  };
  useEffect(() => {
    statusAliveRef.current = true;
    void refreshAiStatus();
    return () => {
      statusAliveRef.current = false;
    };
  }, []);

  // ── Ref to track save-success timeout ─────────────────────────────────────
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load settings on mount ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getAiSettings();
        if (cancelled) return;
        if ("configured" in resp && resp.configured === false) {
          setEncryptionWarning(resp.warning);
          return;
        }
        const s = resp as AiSettings;
        setSettings(s);
        // Initialize key input display values from the loaded masked keys.
        // Users see the masked form; typing a new value replaces it.
        setKeyInputValues(
          Object.fromEntries(
            PROVIDERS.map((p) => [p, s.providers[p]?.api_key_masked ?? ""]),
          ),
        );
        // Verify each provider's stored default model against the just-fetched
        // response — see the comment above `storedModelWarnings`.
        PROVIDERS.forEach((provider) => {
          const model = s.providers[provider]?.default_model;
          if (!model) return;
          verifyModel(provider, model)
            .then((result) => {
              if (cancelled) return;
              setStoredModelWarnings((prev) => ({
                ...prev,
                [provider]: result.ok
                  ? null
                  : { model, message: result.message },
              }));
            })
            .catch(() => {
              if (cancelled) return;
              setStoredModelWarnings((prev) => ({
                ...prev,
                [provider]: {
                  model,
                  message: t("aiSettings.storedModelUnknownReason"),
                },
              }));
            });
        });
      } catch (err) {
        if (!cancelled) setLoadError(getErrorMessage(err));
      } finally {
        if (!cancelled) {
          setIsLoadingSettings(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Pre-populate model lists on mount ─────────────────────────────────────
  // Quietly fetches models for all providers so dropdowns are pre-populated
  // without requiring "Validar Clave".  Writes ONLY to `preloadedModels` —
  // never to `validateState` — so this fetch cannot race with an in-flight
  // explicit validation.  Responses carrying a warning (missing/invalid key)
  // are skipped: the dropdown stays empty until the admin supplies a valid key.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.allSettled(
        PROVIDERS.map(async (provider) => {
          try {
            const resp: ModelsResponse = await getAiModels(provider);
            if (cancelled) return;
            // Skip if the backend returned a warning (no key / provider error).
            if ((resp.models?.length ?? 0) > 0 && !resp.warning) {
              setPreloadedModels((prev) => ({
                ...prev,
                [provider]: resp.models,
              }));
            }
          } catch {
            // Silent — user can still click "Validar Clave" to retry manually.
          }
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleKeyInput = (provider: string, value: string) => {
    setKeyInputValues((prev) => ({ ...prev, [provider]: value }));
  };

  const handleDefaultModel = (provider: Provider, value: string) => {
    setSettings((prev) => ({
      ...prev,
      providers: {
        ...prev.providers,
        [provider]: { ...prev.providers[provider], default_model: value },
      },
    }));
  };

  const handleActiveProvider = (value: string) => {
    setSettings((prev) => ({ ...prev, active_provider: value }));
  };

  const handleToggle = (key: FeatureKey, value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      feature_toggles: { ...prev.feature_toggles, [key]: value },
    }));
  };

  const handleDoubleRead = (value: boolean) => {
    setSettings((prev) => ({ ...prev, match_sheet_import_double_read: value }));
  };

  const handleAllowlistChange = (tool: string, checked: boolean) => {
    setSettings((prev) => ({
      ...prev,
      nl_query_tool_allowlist: checked
        ? [...prev.nl_query_tool_allowlist, tool]
        : prev.nl_query_tool_allowlist.filter((t) => t !== tool),
    }));
  };

  const handleValidateKey = async (provider: Provider) => {
    setValidateState((prev) => ({
      ...prev,
      [provider]: { loading: true, ok: null, error: null },
    }));
    // Send the key only when the input holds a NEW plaintext key; the masked
    // value (or blank) means "test the stored key".
    const typed = keyInputValues[provider];
    const apiKey = typed && !isMasked(typed) ? typed : undefined;
    try {
      const resp = await validateAiKey(provider, apiKey);
      const success = resp.ok && (resp.models?.length ?? 0) > 0;
      const errorText = resp.ok
        ? t("aiSettings.noModelsReturned")
        : resp.message;
      setValidateState((prev) => ({
        ...prev,
        [provider]: {
          loading: false,
          ok: success,
          error: success ? null : errorText,
        },
      }));
      setPreloadedModels((prev) => ({
        ...prev,
        [provider]: success ? resp.models : [],
      }));
      if (success && resp.models.length > 0) {
        // Auto-select the first model if none is already saved.
        setSettings((prev) => ({
          ...prev,
          providers: {
            ...prev.providers,
            [provider]: {
              ...prev.providers[provider],
              default_model:
                prev.providers[provider]?.default_model || resp.models[0].id,
            },
          },
        }));
      }
    } catch (err) {
      setValidateState((prev) => ({
        ...prev,
        [provider]: {
          loading: false,
          ok: false,
          error: getErrorMessage(err),
        },
      }));
      setPreloadedModels((prev) => ({ ...prev, [provider]: [] }));
    }
  };

  const handleRemoveKey = async (provider: Provider) => {
    if (removingKey) return;
    setRemovingKey(provider);
    setRemoveError(null);
    try {
      const updated = await putAiSettings({
        providers: { [provider]: { remove_api_key: true } },
      });
      // Merge ONLY the removed provider's entry: the reply carries the whole
      // server doc, which would discard unsaved edits elsewhere on the page.
      setSettings((prev) => ({
        ...prev,
        providers: {
          ...prev.providers,
          [provider]: updated.providers[provider],
        },
      }));
      setKeyInputValues((prev) => ({
        ...prev,
        [provider]: updated.providers[provider]?.api_key_masked ?? "",
      }));
      setPreloadedModels((prev) => ({ ...prev, [provider]: [] }));
      setValidateState((prev) => ({
        ...prev,
        [provider]: { loading: false, ok: null, error: null },
      }));
      setConfirmingRemove(null);
      setSaveSuccess(false);
      setRemoveSuccess(provider);
    } catch (err) {
      setRemoveError(getErrorMessage(err));
    } finally {
      setRemovingKey(null);
    }
  };

  /** Quietly re-fetch the stored-key model list after a save sent a key. */
  const refreshModels = async (provider: Provider) => {
    try {
      const resp: ModelsResponse = await getAiModels(provider);
      if ((resp.models?.length ?? 0) > 0 && !resp.warning) {
        setPreloadedModels((prev) => ({ ...prev, [provider]: resp.models }));
      }
    } catch {
      // Silent: the admin can still press Validate.
    }
  };

  const handleSave = async () => {
    if (isSaving || encryptionWarning) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const body: AiSettingsPutBody = {
      active_provider: settings.active_provider,
      feature_toggles: settings.feature_toggles,
      nl_query_tool_allowlist: settings.nl_query_tool_allowlist,
      match_sheet_import_double_read:
        settings.match_sheet_import_double_read ?? true,
      providers: {},
    };

    const sentKeyProviders: Provider[] = [];
    for (const p of PROVIDERS) {
      const providerPayload: { api_key?: string; default_model?: string } = {};
      // Only include api_key when the admin typed a NEW plaintext key.
      // NEVER send the masked value (starts with "****") back to the server.
      const rawKey = keyInputValues[p];
      if (rawKey && !isMasked(rawKey)) {
        providerPayload.api_key = rawKey;
        sentKeyProviders.push(p);
      }
      const dm = settings.providers[p]?.default_model;
      if (dm) providerPayload.default_model = dm;
      if (Object.keys(providerPayload).length > 0) {
        body.providers![p] = providerPayload;
      }
    }

    try {
      const updated = await putAiSettings(body);
      // Replace form state with the server-returned masked doc.
      setSettings(updated);
      // Reset key input fields to the new masked values from the server response.
      // The plaintext key the admin typed is discarded from state here.
      setKeyInputValues(
        Object.fromEntries(
          PROVIDERS.map((p) => [p, updated.providers[p]?.api_key_masked ?? ""]),
        ),
      );
      setSaveSuccess(true);
      setRemoveSuccess(null);
      void refreshAiStatus();
      // Fill the dropdown without a second click for every key just sent.
      sentKeyProviders.forEach((p) => void refreshModels(p));
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      // EPIC-95 amendment: a changed default_model runs a live forced
      // tool-call probe before saving; a 422 means it failed the probe and
      // nothing was saved — surface the backend's message naming the model
      // rather than the generic error text.
      const modelError = getModelNotToolCapableDetail(err);
      setSaveError(
        modelError
          ? t("aiSettings.modelNotToolCapableError", {
              model: modelError.model,
              reason: modelError.message,
            })
          : getErrorMessage(err),
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoadingSettings) {
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-gray-50"
        role="status"
        aria-live="polite"
        data-testid="ai-settings-loading"
      >
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-gray-50"
        data-testid="ai-settings-error"
      >
        <div className="text-sm text-gray-500">
          {t("errorFetchingData")}: {loadError}
        </div>
      </div>
    );
  }

  return (
    <div
      className="container mx-auto px-4 py-8 max-w-4xl"
      data-testid="ai-settings-view"
    >
      {/* Page header */}
      <div className="mb-6">
        <h1
          className="text-3xl font-bold text-gray-900"
          data-testid="ai-settings-heading"
        >
          {t("aiSettings.title")}
        </h1>
        <p className="text-gray-600 mt-2">{t("aiSettings.subtitle")}</p>
      </div>

      {/* AI on/off status (EPIC-91, contract v2) */}
      {aiStatus &&
        (() => {
          const reasons = aiStatus.features
            .map((f) => f.reason)
            .concat(aiStatus.reason)
            .filter((r): r is string => !!r);
          const noKeyLine = !aiStatus.provider_key_present &&
            aiStatus.provider && (
              <p className="mt-1" data-testid="ai-status-no-key">
                {t("aiSettings.statusNoProviderKey", {
                  provider: aiStatus.provider,
                })}
              </p>
            );
          if (!aiStatus.active) {
            return (
              <div
                role="status"
                className="mb-6 rounded-lg border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-800"
                data-testid="ai-status-off"
              >
                <p className="font-medium">{t("aiSettings.statusOffTitle")}</p>
                {aiStatus.reason ? (
                  <p className="mt-1">
                    {localizeStatusReason(aiStatus.reason, t)}
                  </p>
                ) : (
                  <AiFeatureRows status={aiStatus} t={t} />
                )}
                {noKeyLine}
                {reasons.some(isAdminFixable) && (
                  <p className="mt-1 text-slate-700">
                    {t("aiSettings.statusOffHint")}
                  </p>
                )}
                {reasons.some((r) => !isAdminFixable(r)) && (
                  <p className="mt-1 text-slate-700">
                    {t("aiSettings.statusOperatorHint")}
                  </p>
                )}
              </div>
            );
          }
          return (
            <div
              role="status"
              className="mb-6 text-sm text-emerald-800"
              data-testid="ai-status-on"
            >
              <p className="font-medium">{t("aiSettings.statusOn")}</p>
              <AiFeatureRows status={aiStatus} t={t} />
              {noKeyLine}
            </div>
          );
        })()}

      {/* Encryption key warning banner */}
      {encryptionWarning && (
        <div
          role="alert"
          className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-testid="encryption-warning"
        >
          <p className="font-medium">{t("aiSettings.encryptionWarning")}</p>
          <p className="mt-1">{encryptionWarning}</p>
        </div>
      )}

      {/* Settings form */}
      <div className="space-y-6">
        {/* Active provider */}
        <section
          className="rounded-xl border border-gray-200 bg-white p-6"
          data-testid="active-provider-section"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("aiSettings.activeProvider")}
          </h2>
          <div className="flex flex-wrap gap-3">
            {PROVIDERS.map((p) => (
              <label key={p} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="active-provider"
                  value={p}
                  checked={settings.active_provider === p}
                  onChange={() => handleActiveProvider(p)}
                  disabled={!!encryptionWarning}
                  className="text-primary-600"
                  data-testid={`provider-radio-${p}`}
                />
                <span className="text-sm font-medium text-gray-700 capitalize">
                  {PROVIDER_LABELS[p]}
                </span>
              </label>
            ))}
          </div>
        </section>

        {/* Provider API keys */}
        <section
          className="rounded-xl border border-gray-200 bg-white p-6"
          data-testid="provider-keys-section"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("aiSettings.providerKeys")}
          </h2>
          <div className="space-y-6">
            {PROVIDERS.map((p) => {
              const provCfg = settings.providers[p];
              const vs = validateState[p];

              return (
                <div
                  key={p}
                  className="border-b border-gray-100 pb-6 last:border-0 last:pb-0"
                  data-testid={`provider-${p}`}
                >
                  <h3 className="text-sm font-semibold text-gray-800 capitalize mb-3">
                    {PROVIDER_LABELS[p]}
                  </h3>

                  {/* API key field */}
                  <div className="mb-3">
                    <label
                      htmlFor={`key-${p}`}
                      className="block text-xs font-medium text-gray-600 mb-1"
                    >
                      {t("aiSettings.apiKey")}
                    </label>
                    <div className="flex gap-2">
                      <input
                        id={`key-${p}`}
                        type="password"
                        autoComplete="new-password"
                        value={keyInputValues[p] ?? ""}
                        onChange={(e) => handleKeyInput(p, e.target.value)}
                        placeholder={t("aiSettings.apiKeyPlaceholder")}
                        disabled={!!encryptionWarning}
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:bg-gray-50"
                        data-testid={`api-key-input-${p}`}
                      />
                      <button
                        type="button"
                        onClick={() => handleValidateKey(p)}
                        disabled={vs.loading || !!encryptionWarning}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`validate-btn-${p}`}
                      >
                        {vs.loading
                          ? t("aiSettings.validating")
                          : t("aiSettings.validateKey")}
                      </button>
                    </div>

                    {/* Remove saved key (EPIC-115): only when a key is stored */}
                    {provCfg?.api_key_masked &&
                      (confirmingRemove === p ? (
                        <div
                          className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-700"
                          role="group"
                          data-testid={`remove-key-confirm-${p}`}
                        >
                          <span>{t("aiSettings.removeKeyConfirm")}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveKey(p)}
                            disabled={removingKey === p}
                            className="rounded-lg border border-red-300 bg-white px-2.5 py-1 font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
                            data-testid={`remove-key-confirm-yes-${p}`}
                          >
                            {t("aiSettings.removeKeyConfirmYes")}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingRemove(null)}
                            disabled={removingKey === p}
                            className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
                            data-testid={`remove-key-cancel-${p}`}
                          >
                            {t("aiSettings.removeKeyCancel")}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setRemoveError(null);
                            setConfirmingRemove(p);
                          }}
                          disabled={!!encryptionWarning || !!removingKey}
                          className="mt-2 text-xs font-medium text-red-700 underline hover:text-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
                          data-testid={`remove-key-btn-${p}`}
                        >
                          {t("aiSettings.removeKey")}
                        </button>
                      ))}
                    {removeSuccess === p && (
                      <p
                        className="mt-1.5 text-xs text-green-700"
                        data-testid={`remove-key-success-${p}`}
                      >
                        {t("aiSettings.removeKeySuccess")}
                      </p>
                    )}
                    {confirmingRemove === p && removeError && (
                      <p
                        className="mt-1.5 text-xs text-red-600"
                        role="alert"
                        data-testid={`remove-key-error-${p}`}
                      >
                        {removeError}
                      </p>
                    )}

                    {/* Validate feedback */}
                    {vs.ok === true && (
                      <p
                        className="mt-1.5 text-xs text-green-700 flex items-center gap-1"
                        data-testid={`validate-ok-${p}`}
                      >
                        <span aria-hidden="true">&#x2713;</span>
                        {t("aiSettings.validateSuccess")}
                      </p>
                    )}
                    {vs.ok === false && vs.error && (
                      <p
                        className="mt-1.5 text-xs text-red-600"
                        data-testid={`validate-error-${p}`}
                      >
                        {vs.error}
                      </p>
                    )}
                  </div>

                  {/* Model selector */}
                  <div>
                    <label
                      htmlFor={`model-${p}`}
                      className="block text-xs font-medium text-gray-600 mb-1"
                    >
                      {t("aiSettings.defaultModel")}
                    </label>
                    <Select
                      value={provCfg?.default_model || UNSET}
                      onValueChange={(next) =>
                        handleDefaultModel(p, next === UNSET ? "" : next)
                      }
                      disabled={!!encryptionWarning}
                    >
                      <SelectTrigger
                        id={`model-${p}`}
                        className="w-full max-w-xs"
                        data-testid={`model-select-${p}`}
                      >
                        <SelectValue>
                          {provCfg?.default_model ||
                            t("aiSettings.selectModel")}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNSET}>
                          {t("aiSettings.selectModel")}
                        </SelectItem>
                        {preloadedModels[p].map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            <span className="flex items-center gap-1.5">
                              <span>{m.id}</span>
                              {m.supports_images && (
                                <ImageCapableBadge
                                  provider={p}
                                  modelId={m.id}
                                  label={t("aiSettings.supportsImages")}
                                />
                              )}
                            </span>
                          </SelectItem>
                        ))}
                        {/* Keep a previously-saved model visible even if it
                            is not in the current preloaded (tool-capable)
                            list — e.g. retired by the provider, or a stale
                            selection saved before this epic's filtering
                            shipped. The warning below explains why. */}
                        {provCfg?.default_model &&
                          !preloadedModels[p].some(
                            (m) => m.id === provCfg.default_model,
                          ) && (
                            <SelectItem value={provCfg.default_model}>
                              {provCfg.default_model}
                            </SelectItem>
                          )}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-gray-400">
                      {t("aiSettings.toolCapableModelsHint")}
                    </p>
                    {storedModelWarnings[p] && (
                      <p
                        className="mt-1.5 text-xs text-amber-700"
                        data-testid={`stored-model-warning-${p}`}
                      >
                        {t("aiSettings.storedModelInvalid", {
                          model: storedModelWarnings[p]!.model,
                          reason: storedModelWarnings[p]!.message,
                        })}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Feature toggles */}
        <section
          className="rounded-xl border border-gray-200 bg-white p-6"
          data-testid="feature-toggles-section"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("aiSettings.featureToggles")}
          </h2>
          <div className="space-y-4">
            {FEATURE_KEYS.map((key) => (
              <Toggle
                key={key}
                id={`toggle-${key}`}
                checked={settings.feature_toggles[key] ?? false}
                onChange={(v) => handleToggle(key, v)}
                label={t(`aiSettings.feature_${key}`)}
                disabled={!!encryptionWarning}
              />
            ))}
            <div
              className="ml-14 border-l-2 border-gray-200 pl-4"
              data-testid="match-sheet-import-double-read"
            >
              <Toggle
                id="toggle-match_sheet_import_double_read"
                checked={settings.match_sheet_import_double_read ?? true}
                onChange={handleDoubleRead}
                label={t("aiSettings.matchSheetImportDoubleRead")}
                disabled={!!encryptionWarning}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("aiSettings.matchSheetImportDoubleReadDesc")}
              </p>
            </div>
          </div>
        </section>

        {/* NL query tool allowlist */}
        <section
          className="rounded-xl border border-gray-200 bg-white p-6"
          data-testid="nl-allowlist-section"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {t("aiSettings.nlQueryAllowlist")}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {t("aiSettings.nlQueryAllowlistDesc")}
          </p>
          <div className="space-y-2">
            {TOOL_ALLOWLIST_OPTIONS.map((tool) => (
              <label
                key={tool}
                className="flex items-center gap-3 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={settings.nl_query_tool_allowlist.includes(tool)}
                  onChange={(e) =>
                    handleAllowlistChange(tool, e.target.checked)
                  }
                  disabled={!!encryptionWarning}
                  className="form-checkbox h-4 w-4 text-primary-600 rounded"
                  data-testid={`allowlist-${tool}`}
                />
                <span className="text-sm font-mono text-gray-700">{tool}</span>
              </label>
            ))}
          </div>
        </section>

        {/* Save button + status */}
        <div className="flex items-center gap-4" data-testid="save-row">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !!encryptionWarning}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-btn"
          >
            {isSaving ? t("aiSettings.saving") : t("aiSettings.save")}
          </button>

          {saveSuccess && (
            <p
              className="text-sm text-green-700"
              data-testid="save-success-msg"
            >
              {t("aiSettings.saveSuccess")}
            </p>
          )}
        </div>

        {saveError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            data-testid="save-error"
          >
            {saveError}
          </div>
        )}
      </div>

      {/* Playground section */}
      <div className="mt-8">
        <PlaygroundSection
          activeProvider={settings.active_provider}
          availableModels={availableModels}
        />
      </div>
    </div>
  );
}
